import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
  InputGroup,
  InputGroupText,
  Button,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { NumericFormat } from "react-number-format";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import Footer from "components/VerticalLayout/Footer";
import { get, post, put, uri } from "helpers/api_helper";
import * as api from "../../../constants/api_constants";
import { showErrorToast, showSuccessToast } from "helpers/toastMessages";
import { getAllCountries } from "helpers/utils/locationUtil";
import DragAndDropComponent from "components/FileUploadDrag/DragAndDrop";

function PaymentForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [fetchedData, setFetchedData] = useState({});
  const [employees, setEmployees] = useState([]);
  const [vendors, setVendors] = useState([]); // Added state for vendors
  const [currencies, setCurrencies] = useState([]);
  const [contact, setContact] = useState(true);
  const [uploadedFile, setUploadedFile] = useState(null);

  const mode =
    id && location.pathname.includes("/view-payment")
      ? "view"
      : id
      ? "edit"
      : "add";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchedCountries = await getAllCountries();
        const currencyMap = new Map();
        fetchedCountries.forEach((country) => {
          const currency = country.currency || "USD";
          const label = country.name || "Unknown";
          if (!currencyMap.has(currency)) {
            currencyMap.set(currency, { label, currency });
          }
        });
        const uniqueCurrencies = Array.from(currencyMap.values());
        setCurrencies(uniqueCurrencies);
      } catch (error) {
        console.error("Error fetching countries:", error);
        showErrorToast("Failed to fetch currencies.");
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const doFetchCalls = async () => {
      await employeeData();
      await fetchVendors(); // Fetch vendors
      if (id) await fetchPaymentData(id);
    };
    doFetchCalls();
  }, [id]);

  const handleFileUpload = (response) => {
    setUploadedFile({ ...response, name: response.fileName });
  };

  const handleFileRemove = () => {
    setUploadedFile(null);
  };

  const employeeData = async () => {
    try {
      const response = await get(api.GET_ALL_EMPLOYEES_NAME_ID_MAIL);
      const employeeResponse = response.map((item) => ({
        id: item.employeeId || "",
        value: item.employeeId || "",
        label: `${item.firstName || ""} ${item.lastName || ""}`.trim() || "Unknown",
        number: item.number || "",
        email: item.email || "",
      }));
      setEmployees(employeeResponse);
    } catch (error) {
      console.error("Error fetching employees data:", error);
      showErrorToast("Failed to fetch employees data.");
    }
  };

  // Fetch vendors from GET_ALL_VENDORS_LIGHT_API
  const fetchVendors = async () => {
    try {
      const response = await get(api.GET_ALL_VENDORS_LIGHT_API);
      const vendorResponse = response.data.map((item) => ({
        id: item.vendorId || "",
        value: item.vendorId || "",
        label: item.companyName || "Unknown",
        primaryPhone: item.primaryPhone || "",
      }));
      setVendors(vendorResponse);
    } catch (error) {
      console.error("Error fetching vendors data:", error);
      showErrorToast("Failed to fetch vendors data.");
    }
  };

  const fetchPaymentData = async (id) => {
    try {
      const response = await get(api.GET_PAYMENT_BY_ID(id));
      const documents = response?.data?.attachments
        ? typeof response.data.attachments === "string"
          ? response.data.attachments.split(",").filter((id) => id)
          : response.data.attachments
        : [];
      if (documents.length > 0) {
        try {
          const fileResponse = await get(
            `${uri}/fileservice/api/files/view/${documents[0]}`
          );
          setUploadedFile({
            id: String(fileResponse.id),
            fileName: fileResponse.fileName,
            name: fileResponse.fileName,
          });
        } catch (err) {
          console.error(`Error fetching file ${documents[0]}:`, err);
          showErrorToast(`Failed to fetch file with ID ${documents[0]}.`);
          setUploadedFile(null);
        }
      } else {
        setUploadedFile(null);
      }

      const paymentData = response?.data || {};
      const normalizedData = {
        paidTo: paymentData.paidTo || "",
        name:
          paymentData.vendorName ||
          paymentData.employeeName ||
          paymentData.otherName ||
          "",
        primaryContact: paymentData.primaryContact || "",
        purpose: paymentData.purpose || "",
        amountToBePaid: paymentData.amountToBePaid
          ? parseFloat(paymentData.amountToBePaid).toFixed(2)
          : "",
        paidAmount: paymentData.paidAmount
          ? parseFloat(paymentData.paidAmount).toFixed(2)
          : "",
        dueAmount: paymentData.dueAmount
          ? parseFloat(paymentData.dueAmount).toFixed(2)
          : "",
        dueDate: paymentData.dueDate ? paymentData.dueDate.slice(0, 10) : "",
        paidDate: paymentData.paidDate ? paymentData.paidDate.slice(0, 10) : "",
        transactionMode: paymentData.transactionMode || "",
        transactionNumber: paymentData.transaction || "",
        attachment: documents,
        refundable:
          paymentData.refundable &&
          paymentData.refundable.toLowerCase() === "yes"
            ? "YES"
            : "NO",
        status: paymentData.status || "",
        notes: paymentData.notes || "",
        currency: paymentData.currency || "USD",
      };
      setFetchedData(normalizedData);
    } catch (error) {
      console.error("Error fetching payment data:", error);
      showErrorToast("Failed to fetch payment data.");
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: id
      ? fetchedData
      : {
          paidTo: "",
          name: "",
          primaryContact: "",
          amountToBePaid: "",
          paidAmount: "",
          dueAmount: "",
          dueDate: "",
          paidDate: "",
          purpose: "",
          transactionMode: "",
          transactionNumber: "",
          attachment: [],
          notes: "",
          refundable: "NO",
          status: "",
          currency: "USD",
        },
    validationSchema: Yup.object({
      paidTo: Yup.string().required("Paid To is required"),
      name: Yup.string().when("paidTo", {
        is: (value) => ["VENDOR", "EMPLOYEE", "OTHERS"].includes(value),
        then: (schema) => schema.required("Name is required"),
        otherwise: (schema) => schema,
      }),
      primaryContact: Yup.string().when("paidTo", {
        is: (value) => value === "OTHERS", // Only required for OTHERS
        then: (schema) => schema.required("Primary Contact is required"),
        otherwise: (schema) => schema,
      }),
      amountToBePaid: Yup.number()
        .required("Amount To Be Paid is required")
        .positive("Amount must be positive"),
      paidAmount: Yup.number()
        .required("Paid Amount is required")
        .min(0, "Paid Amount cannot be negative"),
      dueDate: Yup.date().required("Due Date is required"),
      purpose: Yup.string().required("Purpose is required"),
      transactionMode: Yup.string().required("Transaction Mode is required"),
      status: Yup.string().required("Status is required"),
    }),
    onSubmit: async (values) => {
      if (mode === "view") return;
      try {
        if (
          ["PAID", "PARTIALLY_PAID"].includes(values.status) &&
          !values.paidDate
        ) {
          formik.setFieldError(
            "paidDate",
            "Paid Date is required when status is Paid or Partially Paid"
          );
          showErrorToast(
            "Paid Date is required for Paid or Partially Paid status."
          );
          return;
        }

        const formatDateWithTimezone = (date) => {
          if (!date) return "";
          const offset = "+05:30";
          return new Date(date).toISOString().replace("Z", offset);
        };

        const updatedValues = {
          ...values,
          paidDate:
            ["PAID", "PARTIALLY_PAID"].includes(values.status) &&
            !values.paidDate
              ? new Date().toISOString().slice(0, 10)
              : values.paidDate,
          dueDate: values.dueDate ? formatDateWithTimezone(values.dueDate) : "",
          paidDate: values.paidDate
            ? formatDateWithTimezone(values.paidDate)
            : "",
          amountToBePaid: values.amountToBePaid
            ? parseFloat(values.amountToBePaid).toFixed(2)
            : "0.00",
          paidAmount: values.paidAmount
            ? parseFloat(values.paidAmount).toFixed(2)
            : "0.00",
          dueAmount: values.dueAmount
            ? parseFloat(values.dueAmount).toFixed(2)
            : "0.00",
          attachment: uploadedFile ? [String(uploadedFile.id)] : [],
        };

        let vendorName = null;
        let employeeName = null;
        let otherName = null;
        if (updatedValues.paidTo === "EMPLOYEE") {
          const selectedEmployee = employees.find(
            (employee) => employee.label === updatedValues.name
          );
          employeeName = selectedEmployee
            ? selectedEmployee.label
            : updatedValues.name;
        } else if (updatedValues.paidTo === "VENDOR") {
          const selectedVendor = vendors.find(
            (vendor) => vendor.label === updatedValues.name
          );
          vendorName = selectedVendor
            ? selectedVendor.label
            : updatedValues.name;
        } else if (updatedValues.paidTo === "OTHERS") {
          otherName = updatedValues.name;
        }

        const paymentData = {
          payouts: {
            paidTo: updatedValues.paidTo,
            vendorName,
            employeeName,
            otherName,
            invoice: updatedValues.transactionNumber,
            primaryContact: updatedValues.primaryContact,
            currency: updatedValues.currency,
            amountToBePaid: parseFloat(updatedValues.amountToBePaid) || 0,
            paidAmount: parseFloat(updatedValues.paidAmount) || 0,
            dueAmount: parseFloat(updatedValues.dueAmount) || 0,
            dueDate: updatedValues.dueDate,
            paidDate: updatedValues.paidDate,
            transaction: updatedValues.transactionNumber,
            transactionMode: updatedValues.transactionMode,
            purpose: updatedValues.purpose,
            attachments: updatedValues.attachment.join(",") || "",
            refundable: updatedValues.refundable,
            status: updatedValues.status,
            notes: updatedValues.notes,
            delFlag: 1,
          },
        };

        const updatePaymentData = {
          paidTo: updatedValues.paidTo,
          vendorName,
          employeeName,
          otherName,
          invoice: updatedValues.transactionNumber,
          primaryContact: updatedValues.primaryContact,
          currency: updatedValues.currency,
          amountToBePaid: parseFloat(updatedValues.amountToBePaid) || 0,
          paidAmount: parseFloat(updatedValues.paidAmount) || 0,
          dueAmount: parseFloat(updatedValues.dueAmount) || 0,
          dueDate: updatedValues.dueDate,
          paidDate: updatedValues.paidDate,
          transaction: updatedValues.transactionNumber,
          transactionMode: updatedValues.transactionMode,
          purpose: updatedValues.purpose,
          attachments: updatedValues.attachment.join(",") || "",
          refundable: updatedValues.refundable,
          status: updatedValues.status,
          notes: updatedValues.notes,
          delFlag: 1,
        };

        if (id) {
          const response = await put(
            api.UPDATE_PAYMENT_BY_ID(id),
            updatePaymentData
          );
          if (response && response.success) {
            showSuccessToast("Payment updated successfully!");
            navigate("/payments");
          } else {
            showErrorToast(response.message || "Something went wrong.");
          }
        } else {
          const response = await post(api.CREATE_PAYMENT, paymentData);
          if (response && response.success) {
            showSuccessToast("Payment created successfully!");
            navigate("/payments");
          } else {
            showErrorToast(response.message || "Something went wrong.");
          }
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        showErrorToast(error.message || "Something went wrong.");
      }
    },
  });

  useEffect(() => {
    const amountToBePaid = parseFloat(formik.values.amountToBePaid) || 0;
    const paidAmount = parseFloat(formik.values.paidAmount) || 0;
    const dueAmount = amountToBePaid - paidAmount;
    if (!isNaN(dueAmount) && amountToBePaid > 0 && paidAmount >= 0) {
      formik.setFieldValue("dueAmount", dueAmount.toFixed(2));
    } else {
      formik.setFieldValue("dueAmount", "");
    }
  }, [formik.values.amountToBePaid, formik.values.paidAmount]);

  useEffect(() => {
    if (formik.values.paidTo === "EMPLOYEE" && formik.values.name) {
      const selectedEmployee = employees.find(
        (employee) => employee.label === formik.values.name
      );
      if (selectedEmployee) {
        formik.setFieldValue(
          "primaryContact",
          selectedEmployee.number || selectedEmployee.email || ""
        );
      }
      setContact(false);
    } else if (formik.values.paidTo === "VENDOR" && formik.values.name) {
      const selectedVendor = vendors.find(
        (vendor) => vendor.label === formik.values.name
      );
      if (selectedVendor) {
        formik.setFieldValue("primaryContact", selectedVendor.primaryPhone || "");
      }
      setContact(false);
    } else {
      if (formik.values.paidTo !== "EMPLOYEE" && formik.values.paidTo !== "VENDOR") {
        formik.setFieldValue("primaryContact", "");
      }
      setContact(formik.values.paidTo !== "EMPLOYEE" && formik.values.paidTo !== "VENDOR");
    }
  }, [formik.values.paidTo, formik.values.name, employees, vendors]);

  const heading =
    mode === "view"
      ? "View Payable"
      : mode === "edit"
      ? "Update Payable"
      : "Add Payable";

  return (
    <div className="page-content">
      <Container fluid className="py-0 px-0">
        <PageSubHeader
          heading={heading}
          routePath="Home"
          pageName="Payables"
          backPath="/payments"
        />
        <Row className="px-4 mx-0 my-3">
          <Col xs={12} md={12} className="p-0 m-0">
            <Card>
              <CardBody>
                <Form
                  className="row g-3 needs-validation py-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    formik.handleSubmit();
                  }}
                >
                  <Row className="card-group">
                    <Col xs={12} className="card-header">
                      <p className="form-header">Payable Information</p>
                    </Col>
                    <Row className="card-group-body">
                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="paidTo">
                            Paid To{" "}
                            <span className="text-danger">
                              {mode !== "view" && "*"}
                            </span>
                          </Label>
                          <Input
                            name="paidTo"
                            type="select"
                            className="form-control"
                            id="paidTo"
                            value={formik.values.paidTo}
                            onChange={
                              mode === "view" ? undefined : formik.handleChange
                            }
                            onBlur={
                              mode === "view" ? undefined : formik.handleBlur
                            }
                            invalid={
                              mode !== "view" &&
                              formik.touched.paidTo &&
                              !!formik.errors.paidTo
                            }
                            disabled={mode === "view"}
                          >
                            <option value="">Select</option>
                            <option value="VENDOR">Vendor</option>
                            <option value="EMPLOYEE">Employee</option>
                            <option value="OTHERS">Others</option>
                          </Input>
                          {mode !== "view" &&
                          formik.touched.paidTo &&
                          formik.errors.paidTo ? (
                            <FormFeedback>{formik.errors.paidTo}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      {formik.values.paidTo === "VENDOR" && (
                        <Col md={4}>
                          <FormGroup className="mb-3">
                            <Label htmlFor="name">
                              Vendor Name{" "}
                              <span className="text-danger">
                                {mode !== "view" && "*"}
                              </span>
                            </Label>
                            <Input
                              name="name"
                              type="select"
                              className="form-control"
                              id="name"
                              value={formik.values.name}
                              onChange={
                                mode === "view" ? undefined : formik.handleChange
                              }
                              onBlur={
                                mode === "view" ? undefined : formik.handleBlur
                              }
                              invalid={
                                mode !== "view" &&
                                formik.touched.name &&
                                !!formik.errors.name
                              }
                              disabled={mode === "view"}
                            >
                              <option value="">Select</option>
                              {vendors.map((vendor) => (
                                <option key={vendor.id} value={vendor.label}>
                                  {vendor.label}
                                </option>
                              ))}
                            </Input>
                            {mode !== "view" &&
                            formik.touched.name &&
                            formik.errors.name ? (
                              <FormFeedback>{formik.errors.name}</FormFeedback>
                            ) : null}
                          </FormGroup>
                        </Col>
                      )}
                      {formik.values.paidTo === "EMPLOYEE" && (
                        <Col md={4}>
                          <FormGroup className="mb-3">
                            <Label htmlFor="name">
                              Employee Name{" "}
                              <span className="text-danger">
                                {mode !== "view" && "*"}
                              </span>
                            </Label>
                            <Input
                              name="name"
                              type="select"
                              className="form-control"
                              id="name"
                              value={formik.values.name}
                              onChange={
                                mode === "view" ? undefined : formik.handleChange
                              }
                              onBlur={
                                mode === "view" ? undefined : formik.handleBlur
                              }
                              invalid={
                                mode !== "view" &&
                                formik.touched.name &&
                                !!formik.errors.name
                              }
                              disabled={mode === "view"}
                            >
                              <option value="">Select</option>
                              {employees.map((employee) => (
                                <option key={employee.id} value={employee.label}>
                                  {employee.label}
                                </option>
                              ))}
                            </Input>
                            {mode !== "view" &&
                            formik.touched.name &&
                            formik.errors.name ? (
                              <FormFeedback>{formik.errors.name}</FormFeedback>
                            ) : null}
                          </FormGroup>
                        </Col>
                      )}
                      {formik.values.paidTo === "OTHERS" && (
                        <Col md={4}>
                          <FormGroup className="mb-3">
                            <Label htmlFor="name">
                              Name{" "}
                              <span className="text-danger">
                                {mode !== "view" && "*"}
                              </span>
                            </Label>
                            <Input
                              name="name"
                              type="text"
                              className="form-control"
                              id="name"
                              value={formik.values.name}
                              onChange={
                                mode === "view" ? undefined : formik.handleChange
                              }
                              onBlur={
                                mode === "view" ? undefined : formik.handleBlur
                              }
                              invalid={
                                mode !== "view" &&
                                formik.touched.name &&
                                !!formik.errors.name
                              }
                              disabled={mode === "view"}
                            />
                            {mode !== "view" &&
                            formik.touched.name &&
                            formik.errors.name ? (
                              <FormFeedback>{formik.errors.name}</FormFeedback>
                            ) : null}
                          </FormGroup>
                        </Col>
                      )}
                      {(formik.values.paidTo === "VENDOR" ||
                        formik.values.paidTo === "OTHERS") && (
                        <Col md={4}>
                          <FormGroup className="mb-3">
                            <Label htmlFor="primaryContact">
                              Primary Contact
                            </Label>
                            <Input
                              name="primaryContact"
                              type="text"
                              className="form-control"
                              id="primaryContact"
                              value={formik.values.primaryContact}
                              onChange={
                                mode === "view" ? undefined : formik.handleChange
                              }
                              onBlur={
                                mode === "view" ? undefined : formik.handleBlur
                              }
                              invalid={
                                mode !== "view" &&
                                formik.touched.primaryContact &&
                                !!formik.errors.primaryContact
                              }
                              disabled={mode === "view" || !contact}
                            />
                            {mode !== "view" &&
                            formik.touched.primaryContact &&
                            formik.errors.primaryContact ? (
                              <FormFeedback>
                                {formik.errors.primaryContact}
                              </FormFeedback>
                            ) : null}
                          </FormGroup>
                        </Col>
                      )}
                     
                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="currency">
                            Currency{" "}
                            <span className="text-danger">
                              {mode !== "view" && "*"}
                            </span>
                          </Label>
                          <Input
                            name="currency"
                            type="select"
                            className="form-control"
                            id="currency"
                            value={formik.values.currency}
                            onChange={
                              mode === "view" ? undefined : formik.handleChange
                            }
                            onBlur={
                              mode === "view" ? undefined : formik.handleBlur
                            }
                            invalid={
                              mode !== "view" &&
                              formik.touched.currency &&
                              !!formik.errors.currency
                            }
                            disabled={mode === "view"}
                          >
                            <option value="">Select currency</option>
                            {currencies.map((currency) => (
                              <option
                                key={`${currency.label}-${currency.currency}`}
                                value={currency.currency}
                              >
                                {currency.label} - {currency.currency}
                              </option>
                            ))}
                          </Input>
                          {mode !== "view" &&
                          formik.touched.currency &&
                          formik.errors.currency ? (
                            <FormFeedback>
                              {formik.errors.currency}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="amountToBePaid">
                            Amount To Be Paid{" "}
                            <span className="text-danger">
                              {mode !== "view" && "*"}
                            </span>
                          </Label>
                          <InputGroup>
                            <InputGroupText>
                              {formik.values.currency || "USD"}
                            </InputGroupText>
                            <NumericFormat
                              name="amountToBePaid"
                              className={`form-control ${
                                mode !== "view" &&
                                formik.touched.amountToBePaid &&
                                formik.errors.amountToBePaid
                                  ? "is-invalid"
                                  : ""
                              }`}
                              id="amountToBePaid"
                              value={formik.values.amountToBePaid}
                              onValueChange={(values) => {
                                if (mode === "view") return;
                                const value = values.value || "";
                                const normalized =
                                  value && !isNaN(parseFloat(value))
                                    ? parseFloat(value).toFixed(2)
                                    : "";
                                formik.setFieldValue(
                                  "amountToBePaid",
                                  normalized
                                );
                                formik.setFieldTouched("amountToBePaid", true);
                              }}
                              onBlur={() => {
                                if (mode === "view") return;
                                formik.handleBlur({
                                  target: { name: "amountToBePaid" },
                                });
                                formik.validateField("amountToBePaid");
                              }}
                              thousandSeparator={true}
                              decimalScale={2}
                              fixedDecimalScale={true}
                              allowNegative={false}
                              disabled={mode === "view"}
                            />
                          </InputGroup>
                          {mode !== "view" &&
                          formik.touched.amountToBePaid &&
                          formik.errors.amountToBePaid ? (
                            <FormFeedback>
                              {formik.errors.amountToBePaid}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="paidAmount">
                            Paid Amount{" "}
                            <span className="text-danger">
                              {mode !== "view" && "*"}
                            </span>
                          </Label>
                          <InputGroup>
                            <InputGroupText>
                              {formik.values.currency || "USD"}
                            </InputGroupText>
                            <NumericFormat
                              name="paidAmount"
                              className={`form-control ${
                                mode !== "view" &&
                                formik.touched.paidAmount &&
                                formik.errors.paidAmount
                                  ? "is-invalid"
                                  : ""
                              }`}
                              id="paidAmount"
                              value={formik.values.paidAmount}
                              onValueChange={(values) => {
                                if (mode === "view") return;
                                const value = values.value || "";
                                const normalized =
                                  value && !isNaN(parseFloat(value))
                                    ? parseFloat(value).toFixed(2)
                                    : "";
                                formik.setFieldValue("paidAmount", normalized);
                                formik.setFieldTouched("paidAmount", true);
                              }}
                              onBlur={() => {
                                if (mode === "view") return;
                                formik.handleBlur({
                                  target: { name: "paidAmount" },
                                });
                                formik.validateField("paidAmount");
                              }}
                              thousandSeparator={true}
                              decimalScale={2}
                              fixedDecimalScale={true}
                              allowNegative={false}
                              disabled={mode === "view"}
                            />
                          </InputGroup>
                          {mode !== "view" &&
                          formik.touched.paidAmount &&
                          formik.errors.paidAmount ? (
                            <FormFeedback>
                              {formik.errors.paidAmount}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="dueAmount">Due Amount</Label>
                          <InputGroup>
                            <InputGroupText>
                              {formik.values.currency || "USD"}
                            </InputGroupText>
                            <Input
                              name="dueAmount"
                              type="text"
                              className="form-control"
                              id="dueAmount"
                              value={formik.values.dueAmount}
                              disabled
                            />
                          </InputGroup>
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="paidDate">Paid Date</Label>
                          <Input
                            name="paidDate"
                            type="date"
                            className="form-control"
                            id="paidDate"
                            value={formik.values.paidDate}
                            onChange={
                              mode === "view" ? undefined : formik.handleChange
                            }
                            onBlur={
                              mode === "view" ? undefined : formik.handleBlur
                            }
                            invalid={
                              mode !== "view" &&
                              formik.touched.paidDate &&
                              !!formik.errors.paidDate
                            }
                            disabled={mode === "view"}
                          />
                          {mode !== "view" &&
                          formik.touched.paidDate &&
                          formik.errors.paidDate ? (
                            <FormFeedback>
                              {formik.errors.paidDate}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="dueDate">
                            Due Date{" "}
                            <span className="text-danger">
                              {mode !== "view" && "*"}
                            </span>
                          </Label>
                          <Input
                            name="dueDate"
                            type="date"
                            className="form-control"
                            id="dueDate"
                            value={formik.values.dueDate}
                            onChange={
                              mode === "view" ? undefined : formik.handleChange
                            }
                            onBlur={
                              mode === "view" ? undefined : formik.handleBlur
                            }
                            invalid={
                              mode !== "view" &&
                              formik.touched.dueDate &&
                              !!formik.errors.dueDate
                            }
                            disabled={mode === "view"}
                          />
                          {mode !== "view" &&
                          formik.touched.dueDate &&
                          formik.errors.dueDate ? (
                            <FormFeedback>{formik.errors.dueDate}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="purpose">
                            Purpose{" "}
                            <span className="text-danger">
                              {mode !== "view" && "*"}
                            </span>
                          </Label>
                          <Input
                            name="purpose"
                            type="select"
                            className="form-control"
                            id="purpose"
                            value={formik.values.purpose}
                            onChange={
                              mode === "view" ? undefined : formik.handleChange
                            }
                            onBlur={
                              mode === "view" ? undefined : formik.handleBlur
                            }
                            invalid={
                              mode !== "view" &&
                              formik.touched.purpose &&
                              !!formik.errors.purpose
                            }
                            disabled={mode === "view"}
                          >
                            <option value="">Select Purpose</option>
                            <option value="ADVANCE_PAY_ROLL">
                              Advance Payroll
                            </option>
                            <option value="ATTORNEY_FEE">Attorney Fee</option>
                            <option value="VISA_FEE">Visa Fee</option>
                            <option value="PERSONAL_USE">Personal Use</option>
                            <option value="POSTAGE_FEE">Postage Fees</option>
                            <option value="MISC_FEE">Misc Fees</option>
                          </Input>
                          {mode !== "view" &&
                          formik.touched.purpose &&
                          formik.errors.purpose ? (
                            <FormFeedback>{formik.errors.purpose}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                       <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="transactionNumber">
                            Transaction Number
                          </Label>
                          <Input
                            name="transactionNumber"
                            type="text"
                            className="form-control"
                            id="transactionNumber"
                            value={formik.values.transactionNumber}
                            onChange={
                              mode === "view" ? undefined : formik.handleChange
                            }
                            onBlur={
                              mode === "view" ? undefined : formik.handleBlur
                            }
                            invalid={
                              mode !== "view" &&
                              formik.touched.transactionNumber &&
                              !!formik.errors.transactionNumber
                            }
                            disabled={mode === "view"}
                          />
                          {mode !== "view" &&
                          formik.touched.transactionNumber &&
                          formik.errors.transactionNumber ? (
                            <FormFeedback>
                              {formik.errors.transactionNumber}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="transactionMode">
                            Transaction Mode{" "}
                            <span className="text-danger">
                              {mode !== "view" && "*"}
                            </span>
                          </Label>
                          <Input
                            name="transactionMode"
                            type="select"
                            className="form-control"
                            id="transactionMode"
                            value={formik.values.transactionMode}
                            onChange={
                              mode === "view" ? undefined : formik.handleChange
                            }
                            onBlur={
                              mode === "view" ? undefined : formik.handleBlur
                            }
                            invalid={
                              mode !== "view" &&
                              formik.touched.transactionMode &&
                              !!formik.errors.transactionMode
                            }
                            disabled={mode === "view"}
                          >
                            <option value="">Select Transaction Mode</option>
                            <option value="CHEQUE">Cheque</option>
                            <option value="BANK">Bank</option>
                            <option value="CASH">Cash</option>
                            <option value="CC">Credit/Debit</option>
                          </Input>
                          {mode !== "view" &&
                          formik.touched.transactionMode &&
                          formik.errors.transactionMode ? (
                            <FormFeedback>
                              {formik.errors.transactionMode}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="status">
                            Status{" "}
                            <span className="text-danger">
                              {mode !== "view" && "*"}
                            </span>
                          </Label>
                          <Input
                            name="status"
                            type="select"
                            className="form-control"
                            id="status"
                            value={formik.values.status}
                            onChange={(e) => {
                              if (mode === "view") return;
                              formik.handleChange(e);
                              if (
                                ["PAID", "PARTIALLY_PAID"].includes(
                                  e.target.value
                                ) &&
                                !formik.values.paidDate
                              ) {
                                formik.setFieldValue(
                                  "paidDate",
                                  new Date().toISOString().slice(0, 10)
                                );
                              }
                            }}
                            onBlur={
                              mode === "view" ? undefined : formik.handleBlur
                            }
                            invalid={
                              mode !== "view" &&
                              formik.touched.status &&
                              !!formik.errors.status
                            }
                            disabled={mode === "view"}
                          >
                            <option value="">Select status</option>
                            <option value="PAID">Paid</option>
                            <option value="OVERDUE">Overdue</option>
                            <option value="DRAFT">Draft</option>
                            <option value="PARTIALLY_PAID">
                              Partially Paid
                            </option>
                          </Input>
                          {mode !== "view" &&
                          formik.touched.status &&
                          formik.errors.status ? (
                            <FormFeedback>{formik.errors.status}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="refundable">Refundable</Label>
                          <Input
                            name="refundable"
                            type="checkbox"
                            className="form-check-input"
                            id="refundable"
                            style={{
                              width: "20px",
                              height: "20px",
                              borderWidth: "1px",
                              borderColor: "#000",
                              cursor:
                                mode === "view" ? "not-allowed" : "pointer",
                            }}
                            checked={formik.values.refundable === "YES"}
                            onChange={(e) => {
                              if (mode === "view") return;
                              formik.setFieldValue(
                                "refundable",
                                e.target.checked ? "YES" : "NO"
                              );
                            }}
                            disabled={mode === "view"}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="notes">Notes</Label>
                          <Input
                            name="notes"
                            type="textarea"
                            className="form-control"
                            id="notes"
                            value={formik.values.notes}
                            onChange={
                              mode === "view" ? undefined : formik.handleChange
                            }
                            onBlur={
                              mode === "view" ? undefined : formik.handleBlur
                            }
                            invalid={
                              mode !== "view" &&
                              formik.touched.notes &&
                              !!formik.errors.notes
                            }
                            disabled={mode === "view"}
                          />
                          {mode !== "view" &&
                          formik.touched.notes &&
                          formik.errors.notes ? (
                            <FormFeedback>{formik.errors.notes}</FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Label>Attachment</Label>
                      <Col md={4}>
                        <div className="position-relative rounded py-2 pe-4">
                          <DragAndDropComponent
                            initialFile={uploadedFile || {}}
                            handleFileUpload={handleFileUpload}
                          />
                        </div>
                      </Col>
                    </Row>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Footer
          save={
            mode === "view" ? () => navigate("/payments") : formik.handleSubmit
          }
          saveText={mode === "view" ? "Back" : "Save"}
        />
      </Container>
    </div>
  );
}

export default PaymentForm;